import { IReservation, IReservationDayShift } from '@/interfaces/IReservation';
import { formatTime } from './reservation-time';

export interface IReservationTimeGroup {
  startsAt: string;
  label: string;
  reservations: IReservation[];
}
export interface IReservationDayGroup {
  shift: IReservationDayShift | null;
  reservations: IReservation[];
  timeGroups: IReservationTimeGroup[];
  count: number;
  covers: number;
  capacityRatio: number | null;
}

export function reservationMatchesSearch(
  reservation: Pick<IReservation, 'guestName' | 'guestPhone'>,
  search: string
): boolean {
  const value = search.trim().toLocaleLowerCase();
  const digits = value.replace(/\D/g, '');
  return (
    !value ||
    (reservation.guestName ?? '').toLocaleLowerCase().includes(value) ||
    (!!digits &&
      (reservation.guestPhone ?? '').replace(/\D/g, '').includes(digits))
  );
}

export function isDimmedReservation(
  reservation: Pick<IReservation, 'status'>
): boolean {
  return reservation.status === 'cancelled' || reservation.status === 'no_show';
}

export function groupDayReservations(
  reservations: readonly IReservation[],
  shifts: readonly IReservationDayShift[],
  timeZone: string
): IReservationDayGroup[] {
  const orderedShifts = [...shifts].sort(
    (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
  );
  const buckets = orderedShifts.map((shift) => ({
    shift,
    reservations: [] as IReservation[],
  }));
  const outside: { shift: null; reservations: IReservation[] } = {
    shift: null,
    reservations: [],
  };
  for (const reservation of reservations) {
    const start = new Date(reservation.startsAt).getTime();
    const bucket =
      buckets.find(
        (value) =>
          start >= new Date(value.shift.startsAt).getTime() &&
          start < new Date(value.shift.endsAt).getTime()
      ) ?? outside;
    bucket.reservations.push(reservation);
  }
  return [...buckets, outside]
    .filter((bucket) => bucket.shift || bucket.reservations.length)
    .map((bucket) => {
      const byTime = new Map<number, IReservation[]>();
      for (const reservation of bucket.reservations) {
        const start = new Date(reservation.startsAt).getTime();
        const values = byTime.get(start) ?? [];
        values.push(reservation);
        byTime.set(start, values);
      }
      const timeGroups = [...byTime]
        .sort(([a], [b]) => a - b)
        .map(([start, values]) => ({
          startsAt: new Date(start).toISOString(),
          label: formatTime(start, timeZone),
          reservations: [...values].sort(
            (a, b) =>
              Number(isDimmedReservation(a)) - Number(isDimmedReservation(b)) ||
              a.id - b.id
          ),
        }));
      const active = bucket.reservations.filter(
        (value) => !isDimmedReservation(value)
      );
      return {
        shift: bucket.shift,
        reservations: timeGroups.flatMap((value) => value.reservations),
        timeGroups,
        count: active.length,
        covers: active.reduce((sum, value) => sum + value.partySize, 0),
        capacityRatio: bucket.shift?.maxCovers
          ? bucket.shift.bookedCovers / bucket.shift.maxCovers
          : null,
      };
    });
}

export function reservationStatusCounts(reservations: readonly IReservation[]) {
  return {
    all: reservations.length,
    pending: reservations.filter(
      (value) =>
        value.status === 'pending' || value.status === 'pending_payment'
    ).length,
    upcoming: reservations.filter((value) => value.status === 'confirmed')
      .length,
    present: reservations.filter(
      (value) => value.status === 'arrived' || value.status === 'seated'
    ).length,
    finished: reservations.filter((value) => value.status === 'finished')
      .length,
    cancelled: reservations.filter(isDimmedReservation).length,
  };
}
